/**
 * Recurring transfer schedules for one association.
 *
 * DRAFT — phase 4 of the recurring-transfer epic, written before the job
 * has run anywhere. **Deliberately not routed yet.** The payer file's
 * manager UI came out the opposite of what would have been built without
 * running it first: only three skip reasons ever occurred and two of
 * three daily firings correctly did nothing, so a raw history list would
 * have been mostly noise. This panel has less evidence behind it than
 * that one did, and where it belongs on the page depends on what the
 * data turns out to look like.
 *
 * WHAT IT COMMITS TO ANYWAY, because these are worth keeping whatever
 * the data says:
 *
 *  - **The needs-attention block comes first.** A scheduled transfer
 *    whose outcome is unknown is the one state that requires a person,
 *    and there is currently nowhere it would appear. It says plainly
 *    that it will NOT be retried automatically, because the reasonable
 *    assumption — that a scheduler retries — is exactly the one that
 *    would lead somebody to send the money twice.
 *  - **Three distinct empty states.** "No schedules", "none due yet" and
 *    "due today, not run yet" are different facts. Collapsing them makes
 *    "we never looked" indistinguishable from "nothing to do".
 *  - **The next run date is a column, not a detail.** It is the only
 *    thing on the row that says what will happen rather than what did.
 *
 * Uses the generated client throughout. Regenerated 2026-08-31 once the
 * endpoints were live; `openapi-generator` stays pinned at **7.19.0**,
 * because any other version renames every generated class.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading, Subheading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { Button } from './common/Catalyst/button';
import { bankService } from './clients/ApiClients';
import {
  TransferFrequency,
  TransferReturn,
  TransferScheduleReturn,
  TransferScheduleStatus,
} from './clients/rust-api-client';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canInitiateTransfer } from './constants/UserTypes';
import {
  formatCurrency,
  formatDate,
  getBankErrorParts,
} from './utils/bankHelpers';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import TransferScheduleDialog from './TransferScheduleDialog';

interface Props {
  hoaId: string;
}

type ScheduledTransfer = TransferReturn;

const statusColor = (
  status: TransferScheduleStatus
): 'lime' | 'zinc' | 'amber' => {
  switch (status) {
    case 'active':
      return 'lime';
    case 'paused':
      return 'amber';
    default:
      return 'zinc'; // draft / ended
  }
};

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const ordinal = (n: number): string => {
  if (n > 3 && n < 21) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/**
 * "Monthly on the 1st", "Quarterly on the 15th (Feb, May, Aug, Nov)",
 * with "until 30 Sep 2026" appended when the schedule stops.
 *
 * The end reads as a qualifier on *when*, so it belongs in this column
 * rather than in one of its own — and a schedule that ends is rare
 * enough that a mostly-empty column would be worse than a suffix.
 */
const cadence = (s: TransferScheduleReturn): string => {
  const quarterMonths = [
    'Jan, Apr, Jul, Oct',
    'Feb, May, Aug, Nov',
    'Mar, Jun, Sep, Dec',
  ];
  const every = (() => {
    switch (s.frequency as TransferFrequency) {
      case 'daily':
        return 'Every day';
      case 'weekly':
        return `Weekly on ${WEEKDAYS[(s.day_of_week ?? 1) - 1]}`;
      case 'monthly':
        return `Monthly on the ${ordinal(s.day_of_month ?? 1)}`;
      case 'quarterly':
        return `Quarterly on the ${ordinal(s.day_of_month ?? 1)} (${
          quarterMonths[(s.anchor_month ?? 1) - 1]
        })`;
      default:
        return s.frequency;
    }
  })();
  return s.ends_on ? `${every}, until ${formatDate(s.ends_on)}` : every;
};

const today = (): string => new Date().toISOString().slice(0, 10);

const HoaTransferScheduleList: React.FC<Props> = ({ hoaId }) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canInitiate = useMemo(
    () => canInitiateTransfer(userType),
    [userType]
  );

  const [schedules, setSchedules] = useState<TransferScheduleReturn[]>([]);
  const [transfers, setTransfers] = useState<ScheduledTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setAlert(null);
    try {
      const [scheduleResp, transferResp] = await Promise.all([
        bankService.listTransferSchedules({ hoaId }),
        bankService.listHoaTransfers({ hoaId }),
      ]);
      setSchedules(scheduleResp.data.schedules);
      setTransfers(transferResp.data.transfers);
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setAlert({
        title: "Couldn't load schedules",
        message: body || 'Please try again.',
      });
      setSchedules([]);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const act = async (
    scheduleId: string,
    action: 'confirm' | 'pause' | 'resume' | 'end'
  ) => {
    setBusyId(scheduleId);
    setAlert(null);
    try {
      const args = { hoaId, id: scheduleId };
      if (action === 'end') await bankService.endTransferSchedule(args);
      if (action === 'confirm')
        await bankService.confirmTransferSchedule(args);
      if (action === 'pause') await bankService.pauseTransferSchedule(args);
      if (action === 'resume')
        await bankService.resumeTransferSchedule(args);
      await fetchAll();
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setAlert({
        title:
          action === 'confirm'
            ? "Couldn't activate the schedule"
            : `Couldn't ${action} the schedule`,
        // Resuming re-validates, so this is where "that account was
        // unassigned months ago" surfaces — in front of the person doing
        // it rather than at 06:30 as a failed transfer.
        message: body || 'Please try again.',
      });
    } finally {
      setBusyId(null);
    }
  };

  // A scheduled transfer whose outcome is unknown. The one state that
  // needs a person, and the reason this panel leads with it.
  const needsAttention = useMemo(
    () =>
      transfers.filter(
        (t) => t.status === 'pending' && !!t.schedule_id
      ),
    [transfers]
  );

  const live = schedules.filter(
    (s) => s.status === 'active' || s.status === 'paused'
  );
  const dueToday = live.filter((s) => s.next_occurrences[0] === today());
  const sentToday = new Set(
    transfers
      .filter((t) => t.occurrence_date === today())
      .map((t) => t.schedule_id)
  );
  const awaitingToday = dueToday.filter((s) => !sentToday.has(s.id));

  const scheduleName = (id?: string | null): string =>
    schedules.find((s) => s.id === id)?.name ?? 'a schedule';

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 mb-4">
        <div>
          <Heading level={4}>Recurring transfers</Heading>
          <Text className="mt-1 text-sm">
            Standing instructions to move money between this HOA’s
            accounts. Once activated they run without anyone confirming
            them.
          </Text>
        </div>
        {canInitiate && (
          <Button color="sky" onClick={() => setDialogOpen(true)}>
            <CalendarDaysIcon className="h-5 w-5 mr-2" />
            New Schedule
          </Button>
        )}
      </div>

      {alert && (
        <ShowAlert
          alertType="error"
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={10000}
          isInline={true}
        />
      )}

      {/* ================= NEEDS ATTENTION =================
          First, and styled to be noticed. A transfer here may or may not
          have reached the bank, and the platform will not try again —
          which is the opposite of what somebody would assume about a
          scheduler, and assuming wrong is how the money moves twice. */}
      {needsAttention.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
          <Subheading level={5}>Needs a look</Subheading>
          <Text className="mt-1 text-sm">
            {needsAttention.length === 1
              ? 'A scheduled transfer was sent and we did not get a clear answer back.'
              : `${needsAttention.length} scheduled transfers were sent and we did not get a clear answer back.`}{' '}
            <strong>
              These will not be sent again automatically.
            </strong>{' '}
            Check the account’s activity for the dates below before
            re-sending anything by hand — the bank does not reject a
            duplicate.
          </Text>
          <ul className="mt-3 space-y-2">
            {needsAttention.map((t) => (
              <li key={t.id} className="text-sm">
                <span className="font-medium">
                  {formatCurrency(Number(t.amount))}
                </span>{' '}
                from <span className="tabular-nums">
                  {t.source_account_number}
                </span>{' '}
                to{' '}
                <span className="tabular-nums">
                  {t.dest_account_number}
                </span>
                , for {scheduleName(t.schedule_id)} on{' '}
                {formatDate(t.occurrence_date)}.
                {/* Deliberately not a link. The account's activity lives
                    behind a dialog on the accounts panel, and inventing a
                    route into it for a draft would be guessing at a page
                    layout nobody has seen yet. Naming the account and the
                    date is enough to find it. */}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : schedules.length === 0 ? (
            /* Empty state 1 — nothing has ever been set up. */
            <Text>
              No recurring transfers.
              {canInitiate
                ? ' Use “New Schedule” to move a fixed amount between this HOA’s accounts on a regular day.'
                : ''}
            </Text>
          ) : (
            <>
              {/* Empty states 2 and 3 sit ABOVE the table, because both
                  are statements about a table that is not empty. */}
              {live.length > 0 && dueToday.length === 0 && (
                <Text className="mb-3 text-sm">
                  Nothing is due today. The next transfer is{' '}
                  {formatDate(
                    live
                      .map((s) => s.next_occurrences[0])
                      .filter(Boolean)
                      .sort()[0]
                  )}
                  .
                </Text>
              )}
              {schedules.some((s) => s.status === 'draft') && (
                <Text className="mb-3 text-sm">
                  A draft was set up but never activated. It will not send
                  anything until you activate it.
                </Text>
              )}
              {awaitingToday.length > 0 && (
                <Text className="mb-3 text-sm">
                  {awaitingToday.length === 1
                    ? 'One transfer is due today and has not run yet.'
                    : `${awaitingToday.length} transfers are due today and have not run yet.`}{' '}
                  Transfers are sent in the morning and again in the
                  afternoon.
                </Text>
              )}

              <div className="overflow-x-auto">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                    <Table>
                      <TableHead className="bg-stone-100 dark:bg-gray-600">
                        <TableRow>
                          <TableHeader className="text-base font-semibold">
                            Name
                          </TableHeader>
                          <TableHeader className="text-base font-semibold">
                            From → To
                          </TableHeader>
                          <TableHeader className="text-base font-semibold text-right">
                            Amount
                          </TableHeader>
                          <TableHeader className="text-base font-semibold">
                            When
                          </TableHeader>
                          <TableHeader className="text-base font-semibold">
                            Next
                          </TableHeader>
                          <TableHeader className="text-base font-semibold">
                            Status
                          </TableHeader>
                          {canInitiate && (
                            <TableHeader className="text-base font-semibold" />
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schedules.map((s) => (
                          <TableRow key={s.id}>
                            <TableCell className="font-medium">
                              {s.name}
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {/* An absent number means the account is
                                  gone, and that schedule will not fire.
                                  Saying so beats rendering an opaque id
                                  that looks like data. */}
                              {s.source_account_number ?? (
                                <span className="text-red-600 dark:text-red-400">
                                  account removed
                                </span>
                              )}{' '}
                              →{' '}
                              {s.dest_account_number ?? (
                                <span className="text-red-600 dark:text-red-400">
                                  account removed
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrency(Number(s.amount))}
                            </TableCell>
                            <TableCell>{cadence(s)}</TableCell>
                            <TableCell>
                              {/* An empty list is not "unknown" — it is
                                  "never again", and a paused schedule
                                  saying that would be a lie. */}
                              {s.status === 'paused'
                                ? 'Paused'
                                : s.next_occurrences.length > 0
                                  ? formatDate(s.next_occurrences[0])
                                  : '—'}
                            </TableCell>
                            <TableCell>
                              <Badge color={statusColor(s.status)}>
                                {s.status.charAt(0).toUpperCase() +
                                  s.status.slice(1)}
                              </Badge>
                            </TableCell>
                            {canInitiate && (
                              <TableCell className="text-right whitespace-nowrap">
                                {/* A DRAFT is a schedule somebody
                                    previewed and never activated —
                                    `preview` stores the row, so closing
                                    the dialog leaves one behind. It
                                    fires nothing, so it is harmless, but
                                    without this it can only be deleted
                                    and the work of setting it up is
                                    lost. */}
                                {s.status === 'draft' && (
                                  <Button
                                    plain
                                    disabled={busyId === s.id}
                                    onClick={() => act(s.id, 'confirm')}
                                  >
                                    Activate
                                  </Button>
                                )}
                                {s.status === 'active' && (
                                  <Button
                                    plain
                                    disabled={busyId === s.id}
                                    onClick={() => act(s.id, 'pause')}
                                  >
                                    Pause
                                  </Button>
                                )}
                                {s.status === 'paused' && (
                                  <Button
                                    plain
                                    disabled={busyId === s.id}
                                    onClick={() => act(s.id, 'resume')}
                                  >
                                    Resume
                                  </Button>
                                )}
                                {s.status !== 'ended' && (
                                  <Button
                                    plain
                                    disabled={busyId === s.id}
                                    onClick={() => act(s.id, 'end')}
                                  >
                                    End
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <TransferScheduleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        hoaId={hoaId}
        onCompleted={fetchAll}
      />
    </>
  );
};

export default HoaTransferScheduleList;
